// Redirect.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UniversalTable } from "components/common/common";
import {
  fetchRedirects,
  createRedirect,
  updateRedirect,
  deleteRedirect,
  clearError,
} from "store/redirectsSlice";
import s from "./Redirect.module.css";

const Redirect = () => {
  const dispatch = useDispatch();
  const { items: redirects, loading, error } = useSelector((state) => state.redirects);

  // Состояния для модальных окон
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [currentRecord, setCurrentRecord] = useState({
    id: null,
    redirectlink: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  // Состояния для модального окна подтверждения удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Состояния для уведомлений
  const [notification, setNotification] = useState({
    isOpen: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    dispatch(fetchRedirects());
  }, [dispatch]);

  const showNotification = (message, type = "success") => {
    setNotification({
      isOpen: true,
      message,
      type,
    });

    setTimeout(() => {
      setNotification((prev) => ({ ...prev, isOpen: false }));
    }, 3000);
  };

  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, isOpen: false }));
  };

  const handleCreateNew = () => {
    setModalMode("create");
    setCurrentRecord({
      id: null,
      redirectlink: "",
      description: "",
    });
    setIsModalOpen(true);
  };

  const handleEdit = (rowData) => {
    // rowData содержит данные строки {id, link, description, redirectlink}
    setModalMode("edit");
    setCurrentRecord({
      id: rowData.id,
      redirectlink: rowData.redirectlink,
      description: rowData.description,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentRecord({
      id: null,
      redirectlink: "",
      description: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRecord((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!currentRecord.redirectlink || !currentRecord.description) {
      showNotification("Все поля обязательны для заполнения", "error");
      return;
    }

    // Валидация URL
    if (!isValidUrl(currentRecord.redirectlink)) {
      showNotification("Введите корректный URL", "error");
      return;
    }

    try {
      setSaving(true);

      if (modalMode === "create") {
        await dispatch(createRedirect(currentRecord)).unwrap();
        showNotification("Редирект успешно создан!");
      } else {
        await dispatch(updateRedirect(currentRecord)).unwrap();
        showNotification("Редирект успешно обновлен!");
      }

      handleCloseModal();
    } catch (err) {
      showNotification(`Ошибка: ${err}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (rowData) => {
    // rowData содержит данные строки {id, link, description, redirectlink}
    setRecordToDelete(rowData);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;

    try {
      setDeleting(true);
      await dispatch(deleteRedirect(recordToDelete.id)).unwrap();
      showNotification("Редирект успешно удален!");
      setIsDeleteModalOpen(false);
      setRecordToDelete(null);
    } catch (err) {
      showNotification(`Ошибка удаления: ${err}`, "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setRecordToDelete(null);
  };

  const handleCopy = (link) => {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        showNotification("Ссылка скопирована в буфер обмена!");
      })
      .catch((err) => {
        showNotification("Ошибка копирования: " + err.message, "error");
      });
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  if (loading) {
    return (
      <div className={s.main}>
        <h1 className={s.title}>Управление перенаправлениями</h1>
        <div className={s.loading}>Загрузка редиректов...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={s.main}>
        <h1 className={s.title}>Управление перенаправлениями</h1>
        <div className={s.error}>
          <p>Ошибка при загрузке данных: {error}</p>
          <button
            onClick={() => dispatch(fetchRedirects())}
            className={s.retryButton}
          >
            Попробовать снова
          </button>
          <button
            onClick={() => dispatch(clearError())}
            className={s.retryButton}
          >
            Очистить ошибку
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.main}>
      {/* Кнопка "Создать новый" */}
      <div className={s.addButtonContainer}>
        <h1 className={s.title}>Управление перенаправлениями</h1>
        <button
          onClick={handleCreateNew}
          className={s.addButton}
        >
          + Создать новый
        </button>
      </div>

      {redirects.length === 0 ? (
        <div className={s.emptyState}>
          Нет созданных редиректов. Нажмите "Создать новый" чтобы добавить первый.
        </div>
      ) : (
        <div className={s.tableContainer}>
          <UniversalTable
            headers={["ID", "Ссылка", "Описание", "Редирект на"]}
            rows={redirects.map((redirect) => ({
              id: redirect.id,
              link: redirect.link,
              description: redirect.description,
              redirectlink: redirect.redirectlink,
            }))}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCopy={(rowData) => handleCopy(rowData.link)}
          />
        </div>
      )}

      {/* Модальное окно создания/редактирования */}
      {isModalOpen && (
        <div
          className={s.modalOverlay}
          onClick={handleCloseModal}
        >
          <div
            className={s.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={s.modalHeader}>
              <h2>{modalMode === "create" ? "Создать редирект" : "Редактировать редирект"}</h2>
              <button
                className={s.closeButton}
                onClick={handleCloseModal}
              >
                ×
              </button>
            </div>

            <div className={s.modalBody}>
              <div className={s.inputGroup}>
                <label htmlFor="redirectlink">URL для перенаправления:</label>
                <input
                  type="url"
                  id="redirectlink"
                  name="redirectlink"
                  value={currentRecord.redirectlink}
                  onChange={handleInputChange}
                  className={s.input}
                  placeholder="https://example.com"
                  required
                />
              </div>

              <div className={s.inputGroup}>
                <label htmlFor="description">Описание:</label>
                <textarea
                  id="description"
                  name="description"
                  value={currentRecord.description}
                  onChange={handleInputChange}
                  className={s.textarea}
                  placeholder="Описание редиректа"
                  rows={3}
                  required
                />
              </div>
            </div>

            <div className={s.modalFooter}>
              <button
                onClick={handleCloseModal}
                className={s.cancelButton}
                disabled={saving}
              >
                Отмена
              </button>
              <button
                onClick={handleSave}
                className={s.createButton}
                disabled={saving}
              >
                {saving ? "Сохранение..." : modalMode === "create" ? "Создать" : "Сохранить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {isDeleteModalOpen && (
        <div
          className={s.modalOverlay}
          onClick={handleCancelDelete}
        >
          <div
            className={s.modalContent}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "400px" }}
          >
            <div className={s.modalHeader}>
              <h2>Подтверждение удаления</h2>
              <button
                className={s.closeButton}
                onClick={handleCancelDelete}
              >
                ×
              </button>
            </div>

            <div className={s.modalBody}>
              <p>Вы уверены, что хотите удалить этот редирект?</p>
              {recordToDelete && (
                <div className={s.deleteInfo}>
                  <p>
                    <strong>ID:</strong> {recordToDelete.id}
                  </p>
                  <p>
                    <strong>Ссылка:</strong> {recordToDelete.link}
                  </p>
                  <p>
                    <strong>Редирект на:</strong> {recordToDelete.redirectlink}
                  </p>
                  <p>
                    <strong>Описание:</strong> {recordToDelete.description}
                  </p>
                </div>
              )}
            </div>

            <div className={s.modalFooter}>
              <button
                onClick={handleCancelDelete}
                className={s.cancelButton}
                disabled={deleting}
              >
                Отменить
              </button>
              <button
                onClick={handleConfirmDelete}
                className={s.deleteConfirmButton}
                disabled={deleting}
              >
                {deleting ? "Удаление..." : "Да, удалить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Уведомления */}
      {notification.isOpen && (
        <div
          className={`${s.notification} ${s[`notification_${notification.type}`]}`}
          onClick={closeNotification}
        >
          <div className={s.notificationContent}>
            <span className={s.notificationMessage}>{notification.message}</span>
            <button
              className={s.notificationClose}
              onClick={closeNotification}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Redirect;
